import {
  getExtremePointId,
  TWallExtremePointHostType,
  WallExtremePointEntity
} from '@/editor/entities/wallExtremePointEntity';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { EEntityType, EExtremePointType } from '@/editor/types';
import { FLAGS } from '@/flags';

type TExtremePointData = {
  wallId: string;
  wallType: TWallExtremePointHostType;
  pointType: EExtremePointType;
  position: IPoint2d;
};

type TArcMidPointData = {
  wallId: string;
  wallType: TWallExtremePointHostType;
  start: IPoint2d;
  end: IPoint2d;
  center: IPoint2d;
  clockwise: boolean;
};

export class WallExtremePointManager {
  private _list: WallExtremePointEntity[];
  private _arcMidPointEntity: WallExtremePointEntity | null = null;

  constructor() {
    this._list = [];

    editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this.updateScale);
    editorEvent.on(EditorEventName.ENTITY_SELECTED, this.updateArcMidPoint);
  }

  // 圆弧中点不在这里处理
  private getWallExtremePoints() {
    const relativeWallInfos = globalStores.wallStore.getConnectedWallInfos() || [];

    // 获取自由端端点map
    const extremePoints = new Array<TExtremePointData>();
    const wallDatas = globalStores.wallStore.getWallDatas();
    relativeWallInfos.forEach((relativeWallInfo, index) => {
      const wallData = wallDatas[index];
      if (!wallData) return;
      const { baseData } = wallData;

      const startFree =
        relativeWallInfo.mainStartConnectToPoint.length === 0 && relativeWallInfo.mainStartOnWallInternal.length === 0;
      const endFree =
        relativeWallInfo.mainEndConnectToPoint.length === 0 && relativeWallInfo.mainEndOnWallInternal.length === 0;

      if (startFree) {
        extremePoints.push({
          wallId: relativeWallInfo.id,
          wallType: EEntityType.Wall,
          pointType: EExtremePointType.StartPoint,
          position: baseData.start
        });
      }

      if (endFree) {
        extremePoints.push({
          wallId: relativeWallInfo.id,
          wallType: EEntityType.Wall,
          pointType: EExtremePointType.EndPoint,
          position: baseData.end
        });
      }
    });

    return extremePoints;
  }

  private getSeparateWallExtremePoints() {
    const relativeWallInfos = globalStores.separateWallStore.getConnectedWallInfos(false) || [];
    const separateWallDataMap = new Map(
      globalStores.separateWallStore.separateWallDatas.map((separateWallData) => [
        separateWallData.id,
        separateWallData
      ])
    );
    const extremePoints = new Array<TExtremePointData>();

    relativeWallInfos.forEach((relativeWallInfo) => {
      const separateWallData = separateWallDataMap.get(relativeWallInfo.id);
      if (!separateWallData) return;

      const startFree =
        relativeWallInfo.mainStartConnectToPoint.length === 0 && relativeWallInfo.mainStartOnWallInternal.length === 0;
      const endFree =
        relativeWallInfo.mainEndConnectToPoint.length === 0 && relativeWallInfo.mainEndOnWallInternal.length === 0;

      if (startFree) {
        extremePoints.push({
          wallId: relativeWallInfo.id,
          wallType: EEntityType.SeparateWall,
          pointType: EExtremePointType.StartPoint,
          position: separateWallData.start
        });
      }

      if (endFree) {
        extremePoints.push({
          wallId: relativeWallInfo.id,
          wallType: EEntityType.SeparateWall,
          pointType: EExtremePointType.EndPoint,
          position: separateWallData.end
        });
      }
    });

    return extremePoints;
  }

  private getDataList() {
    const wallHiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.Wall);
    const wallPoints = this.getWallExtremePoints().filter((point) => !wallHiddenIds.has(point.wallId));
    // 隔墙端点拖动未开放时不生成端点实体，hover / 拾取 / 拖拽整条链路随之失效
    if (!FLAGS.FEATURES.ENABLE_SEP_WALL_POINT_DRAG) return wallPoints;

    const sepWallHiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.SeparateWall);
    const separateWallPoints = this.getSeparateWallExtremePoints().filter(
      (point) => !sepWallHiddenIds.has(point.wallId)
    );

    return [...wallPoints, ...separateWallPoints];
  }

  private getScale(zoomLv: number = globalStores.editorStateStore.sceneInfo.scene2d.camera.zoom * 100) {
    return 100 / zoomLv;
  }

  update() {
    const scale = this.getScale();
    const dataList = this.getDataList();

    // 获取自由端端点map
    const dataMap = new Map(
      dataList.map((data) => [getExtremePointId(data.wallId, data.pointType, data.wallType), data])
    );

    // 删除 / 更新
    for (let i = this._list.length - 1; i > -1; i--) {
      const entity = this._list[i];
      const data = dataMap.get(entity.id);
      if (data) {
        entity.update({ position: data.position, scale });
      } else {
        this.deleteEntity(i);
      }

      dataMap.delete(entity.id);
    }

    // 添加新数据
    for (const [_, data] of dataMap.entries()) {
      const entity = new WallExtremePointEntity(data.wallId, data.pointType, data.wallType);
      entity.update({ position: data.position, scale });
      this.addEntity(entity);
    }

    this.updateArcMidPoint();
  }

  private updateScale = (zoomLv: number) => {
    const scale = this.getScale(zoomLv);
    for (const entity of this._list) {
      entity.updateScale(scale);
    }
    this._arcMidPointEntity?.updateScale(scale);
  };

  private deleteEntity(index: number) {
    this._list[index]?.dispose();
    this._list.splice(index, 1);
  }

  private addEntity(entity: WallExtremePointEntity) {
    this._list.push(entity);
  }

  getEntity(entityId: string) {
    if (this._arcMidPointEntity && this._arcMidPointEntity.id === entityId) {
      return this._arcMidPointEntity;
    }

    return this._list.find((wallExtremePoint) => wallExtremePoint.id === entityId);
  }

  private clearArcMidPoint() {
    this._arcMidPointEntity?.dispose();
    this._arcMidPointEntity = null;
  }

  private getSelectedArcMidPointData(): TArcMidPointData | null {
    const globalSelected = globalStores.editorStateStore.selected;

    if (globalSelected.type === EEntityType.Wall) {
      const wallData = globalStores.wallStore.getWallData(globalSelected.entityId);
      if (!wallData) return null;

      const { start, end, center, clockwise } = wallData.baseData;
      if (!center || clockwise === undefined) return null;

      return {
        wallId: wallData.id,
        wallType: EEntityType.Wall,
        start,
        end,
        center,
        clockwise
      };
    }

    if (globalSelected.type === EEntityType.SeparateWall) {
      if (!FLAGS.FEATURES.ENABLE_SEP_WALL_POINT_DRAG) return null;

      const separateWallData = globalStores.separateWallStore.getSepData(globalSelected.entityId, false);
      if (!separateWallData) return null;

      const { start, end, center, clockwise } = separateWallData;
      if (!center || clockwise === undefined) return null;

      return {
        wallId: separateWallData.id,
        wallType: EEntityType.SeparateWall,
        start,
        end,
        center,
        clockwise
      };
    }

    return null;
  }

  // 弧墙中间点显示/隐藏
  private updateArcMidPoint = () => {
    const arcMidPointData = this.getSelectedArcMidPointData();
    if (!arcMidPointData) {
      this.clearArcMidPoint();
      return;
    }

    const currentData = this._arcMidPointEntity?.getData();
    if (
      currentData &&
      (currentData.wallId !== arcMidPointData.wallId || currentData.wallType !== arcMidPointData.wallType)
    ) {
      this.clearArcMidPoint();
    }

    if (!this._arcMidPointEntity) {
      this._arcMidPointEntity = new WallExtremePointEntity(
        arcMidPointData.wallId,
        EExtremePointType.ArcMidPoint,
        arcMidPointData.wallType
      );
    }

    const arc = new ArcSeg2d(arcMidPointData);
    const midPoint = arc.getMidPoint();

    this._arcMidPointEntity.update({ position: midPoint, scale: this.getScale() });
  };

  dispose() {
    editorEvent.off(EditorEventName.ZOOM_UPDATE_2D, this.updateScale);
    editorEvent.off(EditorEventName.ENTITY_SELECTED, this.updateArcMidPoint);
    this._list.forEach((entity) => entity.dispose());
    this._list = [];
    this._arcMidPointEntity?.dispose();
    this._arcMidPointEntity = null;
  }
}
